import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { createUserScopedStorageContainerItemInfo } from '../../utils/scoped-storage';
import { ScopedStorage } from '../../services/ScopedStorageService';
import { z } from 'zod';

const onboardingSchema = z.object({
  isOnboardingSkipped: z.boolean().default(false),
});

type OnboardingPreferences = z.infer<typeof onboardingSchema>;

const defaultOnboardingPreferences: OnboardingPreferences = {
  isOnboardingSkipped: false,
};

export class OnboardingScopedStorage {
  private readonly authContextProvider: AuthContextProvider;

  constructor({
    authContextProvider,
  }: {
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
  }) {
    this.authContextProvider = authContextProvider;
  }

  public async getOnboardingPreferences() {
    const containerItemInfo = await createUserScopedStorageContainerItemInfo({
      authContextProvider: this.authContextProvider,
      itemId: 'onboarding-settings',
    });

    const result = await ScopedStorage.readScopedStorageJsonItem<OnboardingPreferences>(containerItemInfo);

    const parsedResult = onboardingSchema.safeParse(result?.content);

    if (result && !parsedResult.success) {
      console.warn('Onboarding preferences are malformed, resetting to default');
      await ScopedStorage.removeScopedStorageJsonItem(containerItemInfo);
      await ScopedStorage.writeScopedStorageJsonItem({
        ...containerItemInfo,
        content: defaultOnboardingPreferences,
      });
      return defaultOnboardingPreferences;
    }

    if (!parsedResult.success) {
      return defaultOnboardingPreferences;
    }

    return parsedResult.data;
  }

  public async skipOnboarding() {
    const preferences = await this.getOnboardingPreferences();
    const containerItemInfo = await createUserScopedStorageContainerItemInfo({
      authContextProvider: this.authContextProvider,
      itemId: 'onboarding-settings',
    });

    await ScopedStorage.writeScopedStorageJsonItem({
      ...containerItemInfo,
      content: {
        ...preferences,
        isOnboardingSkipped: true,
      },
    });
  }
}
